/*
 * Hounds & Co — engagement layer v2
 * Animated counters, add-to-cart celebration, free-delivery unlock confetti,
 * sticky add-to-cart bar, recently-viewed products, back-to-top.
 * Loaded with defer after Dawn's global.js / pubsub.js.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
   * Shared helpers
   * ------------------------------------------------------------------ */
  function spawnBurst(x, y, emojis) {
    if (reducedMotion) return;
    emojis.forEach(function (emoji, i) {
      var particle = document.createElement('span');
      particle.className = 'hounds-paw-particle';
      particle.textContent = emoji;
      particle.setAttribute('aria-hidden', 'true');
      particle.style.left = x + 'px';
      particle.style.top = y + 'px';
      particle.style.setProperty('--paw-x', (Math.random() * 100 - 50).toFixed(0) + 'px');
      particle.style.setProperty('--paw-y', (-40 - Math.random() * 60).toFixed(0) + 'px');
      particle.style.setProperty('--paw-r', (Math.random() * 60 - 30).toFixed(0) + 'deg');
      particle.style.animationDelay = i * 40 + 'ms';
      document.body.appendChild(particle);
      setTimeout(function () {
        particle.remove();
      }, 1200);
    });
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  function storageGet(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key)) || [];
    } catch (e) {
      return [];
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable (private mode) — feature degrades silently */
    }
  }

  /* ------------------------------------------------------------------
   * Animated number counters ([data-hounds-count])
   * ------------------------------------------------------------------ */
  function initCounters() {
    var counters = document.querySelectorAll('[data-hounds-count]');
    if (!counters.length) return;

    var animate = function (el) {
      var target = parseFloat(el.getAttribute('data-hounds-count'));
      if (isNaN(target)) return;
      if (reducedMotion) {
        el.textContent = target.toLocaleString();
        return;
      }
      var duration = 1400;
      var start = null;
      var step = function (ts) {
        if (!start) start = ts;
        var progress = Math.min((ts - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(target * eased).toLocaleString();
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    if (!('IntersectionObserver' in window)) {
      counters.forEach(function (el) {
        el.textContent = parseFloat(el.getAttribute('data-hounds-count')).toLocaleString();
      });
      return;
    }

    var seen = new WeakSet();
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !seen.has(entry.target)) {
            seen.add(entry.target);
            animate(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------
   * Add-to-cart celebration + free-delivery unlock confetti
   * ------------------------------------------------------------------ */
  var shippingBarWasUnlocked = null;

  function celebrateCart() {
    var icon = document.querySelector('#cart-icon-bubble');
    if (!icon) return;

    icon.classList.remove('hounds-cart-bounce');
    void icon.offsetWidth;
    icon.classList.add('hounds-cart-bounce');

    var rect = icon.getBoundingClientRect();
    spawnBurst(rect.left + rect.width / 2, rect.top + rect.height / 2, ['🐾', '🐾', '❤️', '🐾', '✨']);
  }

  function checkShippingUnlock() {
    var bar = document.querySelector('#CartDrawer .hounds-shipping-bar, .cart__items .hounds-shipping-bar');
    if (!bar) {
      shippingBarWasUnlocked = null;
      return;
    }
    var unlocked = bar.classList.contains('hounds-shipping-bar--unlocked');
    if (unlocked && shippingBarWasUnlocked === false) {
      var rect = bar.getBoundingClientRect();
      spawnBurst(rect.left + rect.width / 2, rect.top + rect.height / 2, ['🎉', '🐾', '✨', '🎉', '❤️', '✨']);
    }
    shippingBarWasUnlocked = unlocked;
  }

  function initCartCelebration() {
    // Dawn exposes subscribe/PUB_SUB_EVENTS as top-level bindings from pubsub.js/constants.js
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'product-form') {
            celebrateCart();
          }
          // drawer re-renders asynchronously after the cart update event
          setTimeout(checkShippingUnlock, 400);
        });
      }
    } catch (e) {
      /* celebration is optional — never break the page for it */
    }
    // capture the initial state so we only celebrate a genuine transition
    setTimeout(checkShippingUnlock, 500);
  }

  /* ------------------------------------------------------------------
   * Sticky add-to-cart bar (product pages)
   * ------------------------------------------------------------------ */
  function initStickyAtc() {
    var infoContainer = document.querySelector('.product__info-container');
    if (!infoContainer) return;
    var mainSubmit = infoContainer.querySelector('.product-form__submit');
    if (!mainSubmit) return;

    var titleEl = document.querySelector('.product__info-container .product__title h1')
      || document.querySelector('.product__info-container .product__title');
    var priceEl = infoContainer.querySelector('.price-item--last') || infoContainer.querySelector('.price-item');
    var mediaImg = document.querySelector('.product__media img');

    var bar = document.createElement('div');
    bar.className = 'hounds-sticky-atc';
    var inner = document.createElement('div');
    inner.className = 'hounds-sticky-atc__inner';

    if (mediaImg) {
      var img = document.createElement('img');
      img.className = 'hounds-sticky-atc__image';
      img.src = mediaImg.currentSrc || mediaImg.src;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      inner.appendChild(img);
    }

    var meta = document.createElement('div');
    meta.className = 'hounds-sticky-atc__meta';
    var titleP = document.createElement('p');
    titleP.className = 'hounds-sticky-atc__title';
    titleP.textContent = titleEl ? titleEl.textContent.trim() : document.title;
    var priceP = document.createElement('p');
    priceP.className = 'hounds-sticky-atc__price';
    priceP.textContent = priceEl ? priceEl.textContent.trim() : '';
    meta.appendChild(titleP);
    meta.appendChild(priceP);
    inner.appendChild(meta);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'hounds-sticky-atc__button';

    var syncButton = function () {
      var label = mainSubmit.querySelector('span');
      button.textContent = label ? label.textContent.trim() : 'Add to cart';
      var disabled = mainSubmit.hasAttribute('disabled') || mainSubmit.classList.contains('disabled');
      if (disabled) {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }
      if (priceEl) priceP.textContent = priceEl.textContent.trim();
    };
    syncButton();

    // keep label/state in sync with variant changes
    if ('MutationObserver' in window) {
      new MutationObserver(syncButton).observe(mainSubmit, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    button.addEventListener('click', function () {
      mainSubmit.click();
    });

    inner.appendChild(button);
    bar.appendChild(inner);
    document.body.appendChild(bar);

    var setActive = function (active) {
      bar.classList.toggle('is-active', active);
      document.body.classList.toggle('hounds-atc-visible', active);
    };

    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        // show only once the real buy button has scrolled up out of view
        setActive(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    observer.observe(mainSubmit);
  }

  /* ------------------------------------------------------------------
   * Recently viewed products
   * ------------------------------------------------------------------ */
  var RV_KEY = 'hounds:recently-viewed';

  function recordProductView() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    var handle = decodeURIComponent(match[1]);
    var list = storageGet(RV_KEY).filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    storageSet(RV_KEY, list.slice(0, 12));
    return handle;
  }

  function buildRvCard(product) {
    var card = document.createElement('a');
    card.className = 'hounds-rv-card';
    card.href = product.url;

    var media = document.createElement('div');
    media.className = 'hounds-rv-card__media';
    if (product.featured_image) {
      var img = document.createElement('img');
      var src = product.featured_image;
      img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=480';
      img.alt = product.title;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);

    var info = document.createElement('div');
    info.className = 'hounds-rv-card__info';
    var title = document.createElement('p');
    title.className = 'hounds-rv-card__title';
    title.textContent = product.title;
    var price = document.createElement('p');
    price.className = 'hounds-rv-card__price';
    price.textContent = (product.price_varies ? 'From ' : '') + formatMoney(product.price);
    info.appendChild(title);
    info.appendChild(price);
    card.appendChild(info);

    return card;
  }

  function initRecentlyViewed() {
    var currentHandle = recordProductView();
    var containers = document.querySelectorAll('[data-hounds-rv]');
    if (!containers.length) return;

    var handles = storageGet(RV_KEY).filter(function (h) {
      return h !== currentHandle;
    });
    if (!handles.length) return;

    containers.forEach(function (container) {
      var limit = parseInt(container.getAttribute('data-limit'), 10) || 4;
      var wanted = handles.slice(0, limit);

      Promise.all(
        wanted.map(function (handle) {
          return fetch('/products/' + encodeURIComponent(handle) + '.js')
            .then(function (res) {
              return res.ok ? res.json() : null;
            })
            .catch(function () {
              return null;
            });
        })
      ).then(function (products) {
        var valid = products.filter(Boolean);
        if (!valid.length) return;
        valid.forEach(function (product) {
          container.appendChild(buildRvCard(product));
        });
        var section = container.closest('[data-hounds-rv-section]');
        if (section) section.hidden = false;
      });
    });
  }

  /* ------------------------------------------------------------------
   * Nutrition bar charts — parse Protein/Fat/Ash/etc from descriptions
   * ------------------------------------------------------------------ */
  var NUTRIENT_SRC = '\\b(crude\\s+)?(protein|oils?\\s*(?:&(?:amp;)?|and)\\s*fats?|fat(?:\\s+content)?|ash|fibres?|fibers?|moisture|salt)\\s*:?\\s*(\\d+(?:[.,]\\d+)?)\\s*%';
  var HEADER_WORDS_SRC = 'analytical\\s+constituents?|typical\\s+analysis|nutritional?\\s+(?:information|analysis|values|breakdown)|nutrition|constituents?';

  var NUTRIENT_META = {
    protein: { label: 'Protein', color: '#3d2b23' },
    fat: { label: 'Fat', color: '#b4593e' },
    'oils & fats': { label: 'Oils & fats', color: '#b4593e' },
    fibre: { label: 'Fibre', color: '#8a7351' },
    ash: { label: 'Ash', color: '#a08679' },
    moisture: { label: 'Moisture', color: '#c98d76' },
    salt: { label: 'Salt', color: '#d9bfb2' },
  };

  function nutrientKey(raw) {
    var s = raw.toLowerCase().replace(/\s+/g, ' ').trim();
    if (/^oils?\s*(&(amp;)?|and)\s*fats?$/.test(s)) return 'oils & fats';
    if (/^fat(\s+content)?$/.test(s)) return 'fat';
    if (/^fibres?$|^fibers?$/.test(s)) return 'fibre';
    return s;
  }

  function parseNutrients(text) {
    var re = new RegExp(NUTRIENT_SRC, 'gi');
    var found = [];
    var seen = {};
    var match;
    while ((match = re.exec(text)) !== null) {
      var key = nutrientKey(match[2]);
      if (seen[key]) continue;
      seen[key] = true;
      var value = parseFloat(match[3].replace(',', '.'));
      if (isNaN(value) || value <= 0 || value > 100) continue;
      var meta = NUTRIENT_META[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), color: '#a08679' };
      found.push({ key: key, label: meta.label, color: meta.color, value: value });
    }
    found.sort(function (a, b) {
      return b.value - a.value;
    });
    return found;
  }

  function isNutritionOnly(el) {
    var text = el.textContent;
    if (!text || !text.trim()) return false;
    var stripped = text
      .replace(new RegExp(NUTRIENT_SRC, 'gi'), '')
      .replace(new RegExp(HEADER_WORDS_SRC, 'gi'), '')
      .replace(/[,;:.%()\/\-–—\s]+/g, '');
    return stripped.length <= 2;
  }

  function buildNutritionChart(nutrients) {
    var card = document.createElement('div');
    card.className = 'hounds-nutrition';

    var title = document.createElement('h3');
    title.className = 'hounds-nutrition__title';
    title.innerHTML =
      '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><ellipse cx="6" cy="8.5" rx="1.9" ry="2.5"/><ellipse cx="10.5" cy="5.5" rx="1.9" ry="2.6"/><ellipse cx="15.5" cy="6.5" rx="1.9" ry="2.5"/><ellipse cx="19" cy="10.5" rx="1.8" ry="2.3"/><path d="M12.5 10.5c-2.9 0-6 3.2-6 6.1 0 1.8 1.3 3 3.1 3 1.1 0 2-.6 2.9-.6.9 0 1.8.6 2.9.6 1.8 0 3.1-1.2 3.1-3 0-2.9-3.1-6.1-6-6.1z"/></svg>';
    var titleText = document.createElement('span');
    titleText.textContent = 'Analytical constituents';
    title.appendChild(titleText);
    card.appendChild(title);

    nutrients.forEach(function (n) {
      var row = document.createElement('div');
      row.className = 'hounds-nutrition__row';

      var label = document.createElement('span');
      label.className = 'hounds-nutrition__label';
      label.textContent = n.label;

      var track = document.createElement('div');
      track.className = 'hounds-nutrition__track';
      track.setAttribute('aria-hidden', 'true');
      var fill = document.createElement('div');
      fill.className = 'hounds-nutrition__fill';
      fill.style.background = n.color;
      fill.setAttribute('data-hounds-width', Math.min(n.value, 100));
      track.appendChild(fill);

      var value = document.createElement('span');
      value.className = 'hounds-nutrition__value';
      value.textContent = (n.value % 1 === 0 ? n.value.toFixed(0) : String(n.value)) + '%';

      row.appendChild(label);
      row.appendChild(track);
      row.appendChild(value);
      card.appendChild(row);
    });

    return card;
  }

  function animateNutritionChart(card) {
    var fills = card.querySelectorAll('.hounds-nutrition__fill');
    var reveal = function () {
      fills.forEach(function (fill) {
        fill.style.width = fill.getAttribute('data-hounds-width') + '%';
      });
    };
    if (!('IntersectionObserver' in window) || reducedMotion) {
      reveal();
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          reveal();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(card);
  }

  function initNutritionCharts() {
    document.querySelectorAll('.product__description:not([data-hounds-nutrition])').forEach(function (container) {
      container.setAttribute('data-hounds-nutrition', 'done');

      var nutrients = parseNutrients(container.textContent);
      if (!nutrients.length) return;

      // hide the raw nutrition lines (and their now-empty wrappers)
      container.querySelectorAll('*').forEach(function (el) {
        if (el === container) return;
        if (isNutritionOnly(el)) el.style.display = 'none';
      });

      var chart = buildNutritionChart(nutrients);
      container.appendChild(chart);
      animateNutritionChart(chart);
    });
  }

  /* ------------------------------------------------------------------
   * Compact sticky header — reliably toggle Dawn's scrolled-past-header
   * class (Dawn's own IntersectionObserver-based toggle can miss it).
   * Hysteresis (add at 180px, remove below 60px) prevents flicker when
   * the collapsing header changes the page height.
   * ------------------------------------------------------------------ */
  function initCompactHeader() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var onScroll = function () {
      var y = window.scrollY;
      if (y > 180) {
        header.classList.add('scrolled-past-header');
      } else if (y < 60) {
        header.classList.remove('scrolled-past-header');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
   * Mega menu: open on hover, navigate on click
   * ------------------------------------------------------------------ */
  function initMegaMenuHover() {
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var isDesktop = window.matchMedia('(min-width: 990px)').matches;
    if (!canHover || !isDesktop) return;

    document.querySelectorAll('.header__inline-menu header-menu > details').forEach(function (details) {
      var summary = details.querySelector('summary');
      if (!summary) return;
      var item = details.closest('li') || details;
      var closeTimer = null;

      item.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      });

      item.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(function () {
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }, 250);
      });

      var url = summary.getAttribute('data-hounds-url');
      if (url) {
        summary.addEventListener('click', function (event) {
          // keyboard activation (event.detail === 0) keeps the default
          // open/close toggle so the submenu stays accessible
          if (event.detail === 0) return;
          event.preventDefault();
          event.stopPropagation();
          window.location.href = url;
        });
      }
    });
  }

  /* ------------------------------------------------------------------
   * Treat suggestion popups
   * ------------------------------------------------------------------ */
  function initSuggestPopups() {
    var cfg = window.houndsConfig || {};
    if (!cfg.popupEnabled) return;

    var COUNT_KEY = 'hounds:popup-count';
    var SEEN_KEY = 'hounds:popup-seen';
    var CACHE_KEY = 'hounds:popup-products';
    var EYEBROWS = ['The pack recommends 🐾', 'Customers are loving', 'Have you sniffed out…', 'A favourite right now 🐾'];

    var shownCount = parseInt(sessionStorage.getItem(COUNT_KEY), 10) || 0;
    var maxShown = cfg.popupMax || 3;
    if (shownCount >= maxShown) return;

    var currentMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    var currentHandle = currentMatch ? decodeURIComponent(currentMatch[1]) : null;

    var products = null;

    function loadProducts() {
      if (products) return Promise.resolve(products);
      try {
        var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
        if (cached && cached.length) {
          products = cached;
          return Promise.resolve(products);
        }
      } catch (e) {
        /* fall through to fetch */
      }
      return fetch('/collections/' + encodeURIComponent(cfg.popupCollection || 'best-sellers') + '/products.json?limit=24')
        .then(function (res) {
          return res.ok ? res.json() : { products: [] };
        })
        .then(function (data) {
          products = (data.products || [])
            .filter(function (p) {
              return p.variants && p.variants.length && p.images && p.images.length;
            })
            .map(function (p) {
              return {
                handle: p.handle,
                title: p.title,
                priceCents: Math.round(parseFloat(p.variants[0].price) * 100),
                image: p.images[0].src,
              };
            });
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(products));
          } catch (e) {
            /* cache is best-effort */
          }
          return products;
        })
        .catch(function () {
          return [];
        });
    }

    function pickProduct(list) {
      var seen = storageGet(SEEN_KEY);
      var candidates = list.filter(function (p) {
        return p.handle !== currentHandle && seen.indexOf(p.handle) === -1;
      });
      if (!candidates.length) return null;
      return candidates[Math.floor(Math.random() * candidates.length)];
    }

    function showPopup(product) {
      var popup = document.createElement('aside');
      popup.className = 'hounds-popup';
      popup.setAttribute('aria-label', 'Treat suggestion');

      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'hounds-popup__close';
      close.setAttribute('aria-label', 'Dismiss suggestion');
      close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      popup.appendChild(close);

      var body = document.createElement('a');
      body.className = 'hounds-popup__body';
      body.href = '/products/' + encodeURIComponent(product.handle);

      var img = document.createElement('img');
      img.className = 'hounds-popup__image';
      var src = product.image;
      img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
      img.alt = '';
      img.loading = 'lazy';
      body.appendChild(img);

      var info = document.createElement('div');
      info.className = 'hounds-popup__info';
      var eyebrow = document.createElement('p');
      eyebrow.className = 'hounds-popup__eyebrow';
      eyebrow.textContent = EYEBROWS[shownCount % EYEBROWS.length];
      var title = document.createElement('p');
      title.className = 'hounds-popup__title';
      title.textContent = product.title;
      var price = document.createElement('p');
      price.className = 'hounds-popup__price';
      price.textContent = formatMoney(product.priceCents) + ' · Take a look';
      info.appendChild(eyebrow);
      info.appendChild(title);
      info.appendChild(price);
      body.appendChild(info);
      popup.appendChild(body);

      document.body.appendChild(popup);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          popup.classList.add('is-visible');
        });
      });

      var removePopup = function () {
        popup.classList.remove('is-visible');
        setTimeout(function () {
          popup.remove();
        }, 400);
      };
      close.addEventListener('click', removePopup);
      var autoHide = setTimeout(removePopup, 12000);
      popup.addEventListener('mouseenter', function () {
        clearTimeout(autoHide);
      });

      var seen = storageGet(SEEN_KEY);
      seen.push(product.handle);
      storageSet(SEEN_KEY, seen);
      shownCount++;
      try {
        sessionStorage.setItem(COUNT_KEY, String(shownCount));
      } catch (e) {
        /* best-effort */
      }
    }

    function attempt() {
      if (shownCount >= maxShown) return;
      // wait politely if the tab is hidden or a drawer/modal is open
      if (document.hidden || /overflow-hidden/.test(document.body.className)) {
        setTimeout(attempt, 5000);
        return;
      }
      loadProducts().then(function (list) {
        var product = pickProduct(list);
        if (!product) return;
        showPopup(product);
        if (shownCount < maxShown) {
          setTimeout(attempt, (cfg.popupInterval || 45) * 1000);
        }
      });
    }

    setTimeout(attempt, (cfg.popupFirstDelay || 15) * 1000);
  }

  /* ------------------------------------------------------------------
   * Back-to-top button
   * ------------------------------------------------------------------ */
  function initBackToTop() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hounds-back-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    document.body.appendChild(btn);

    var toggle = function () {
      btn.classList.toggle('is-visible', window.scrollY > 600);
    };
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    });
  }

  /* ------------------------------------------------------------------ */
  function init() {
    initCounters();
    initCartCelebration();
    initStickyAtc();
    initRecentlyViewed();
    initNutritionCharts();
    initCompactHeader();
    initMegaMenuHover();
    initSuggestPopups();
    initBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
